import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import AnimatePresenceWrapper from '../common/AnimatePresenceWrapper';

interface FormInputs {
  name: string;
  email: string;
  subject: string;
  message: string;
}

// Email Services > [Votre Service] > Service ID
const EMAILJS_SERVICE_ID = 'service_b9okj9p';

// Email Templates > [Votre Template] > Template ID
const EMAILJS_TEMPLATE_ID = 'template_qn3l9lw';

// Account > API Keys > Public Key
const EMAILJS_PUBLIC_KEY = 'sNy3osfVDRvPLzwj-';

const LoadingSpinner = () => (
  <motion.div
    className="inline-block"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    />
  </motion.div>
);

const StatusMessage = ({ status }: { status: 'success' | 'error' }) => {
  const variants = {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 }
  };

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`p-4 ${
        status === 'success'
          ? 'bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200'
          : 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200'
      } rounded-md`}
    >
      <motion.div
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        {status === 'success' ? (
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </svg>
            <span>Message envoyé avec succès !</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <motion.path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </svg>
            <span>Une erreur est survenue. Veuillez réessayer.</span>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>();

  const onSubmit = async (data: FormInputs) => {
    setIsSubmitting(true);
    try {
      const templateParams = {
        name: data.name,
        message: data.message,
        time: new Date().toLocaleString('fr-FR', {
          dateStyle: 'long',
          timeStyle: 'short'
        }),
        email: data.email,
        subject: data.subject
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formVariants = {
    submitting: { opacity: 0.7, scale: 0.99 },
    idle: { opacity: 1, scale: 1 }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-lg mx-auto p-6 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50"
    >
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Me contacter
      </h2>

      <motion.form
        variants={formVariants}
        animate={isSubmitting ? "submitting" : "idle"}
        transition={{ duration: 0.2 }}
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Nom
          </label>
          <input
            type="text"
            id="name"
            {...register('name', { required: 'Le nom est requis' })}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors.name
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500'
            } dark:bg-gray-700 dark:text-white`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Email
          </label>
          <input
            type="email"
            id="email"
            {...register('email', {
              required: 'L\'email est requis',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Adresse email invalide',
              },
            })}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors.email
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500'
            } dark:bg-gray-700 dark:text-white`}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="subject"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Sujet
          </label>
          <input
            type="text"
            id="subject"
            {...register('subject', { required: 'Le sujet est requis' })}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors.subject
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500'
            } dark:bg-gray-700 dark:text-white`}
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-red-500">{errors.subject.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Message
          </label>
          <textarea
            id="message"
            rows={4}
            {...register('message', {
              required: 'Le message est requis',
              minLength: {
                value: 10,
                message: 'Le message doit contenir au moins 10 caractères',
              },
            })}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors.message
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:border-indigo-500 focus:ring-indigo-500'
            } dark:bg-gray-700 dark:text-white`}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              isSubmitting
                ? 'bg-indigo-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 space-x-2`}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner />
                <span>Envoi en cours...</span>
              </>
            ) : (
              'Envoyer'
            )}
          </button>
        </div>

        <AnimatePresenceWrapper mode="wait">
          {submitStatus && (
            <StatusMessage status={submitStatus} />
          )}
        </AnimatePresenceWrapper>
      </motion.form>
    </motion.div>
  );
}; 